import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const TaxBracketFinder = () => {
  const content = article.find((item) => item.id === 7);
  const [mode, setMode] = useState("federal");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const currentYear = new Date().getFullYear();


  const [inputs, setInputs] = useState({
    // Basic Information
    filingStatus: "single",
    taxableIncome: "75000",
    taxYear: "2024",
    state: "CA",
    
    // Income Details
    wages: "70000",
    investmentIncome: "5000",
    businessIncome: "0",
    otherIncome: "0",
    
    // Deductions
    standardDeduction: "14600",
    itemizedDeduction: "0",
    useStandardDeduction: true,
    
    // Credits
    childTaxCredit: "0",
    earnedIncomeCredit: "0",
    educationCredits: "0",
    
    // Additional Factors
    age: "35",
    isBlind: false,
    isDisabled: false,
    numberOfDependents: "0"
  });

  // Initialize result with default values to prevent undefined errors
  const [result, setResult] = useState({
    federalBrackets: [],
    stateBrackets: [],
    effectiveTaxRate: 0,
    marginalTaxRate: 0,
    totalTaxLiability: 0,
    afterTaxIncome: 0,
    taxBreakdown: {
      federal: 0,
      state: 0,
      fica: 0,
      additional: 0,
      credits: 0
    },
    nextBracket: {
      threshold: 0,
      additionalTax: 0,
      rate: 0
    },
    taxSavings: {
      standardVsItemized: 0,
      credits: 0
    },
    incomeSummary: {
      agi: 0,
      taxableIncome: 0,
      deduction: 0,
      totalCredits: 0
    },
    bracketProgression: []
  });

  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // 2024 Federal Tax Brackets
  const federalTaxBrackets = {
    single: [
      { min: 0, max: 11600, rate: 0.10, tax: 1160 },
      { min: 11601, max: 47150, rate: 0.12, tax: 4268 },
      { min: 47151, max: 100525, rate: 0.22, tax: 11738 },
      { min: 100526, max: 191950, rate: 0.24, tax: 21942 },
      { min: 191951, max: 243725, rate: 0.32, tax: 16568 },
      { min: 243726, max: 609350, rate: 0.35, tax: 127968 },
      { min: 609351, max: Infinity, rate: 0.37, tax: 0 }
    ],
    married: [
      { min: 0, max: 23200, rate: 0.10, tax: 2320 },
      { min: 23201, max: 94300, rate: 0.12, tax: 8532 },
      { min: 94301, max: 201050, rate: 0.22, tax: 23485 },
      { min: 201051, max: 383900, rate: 0.24, tax: 43884 },
      { min: 383901, max: 487450, rate: 0.32, tax: 33136 },
      { min: 487451, max: 731200, rate: 0.35, tax: 85262 },
      { min: 731201, max: Infinity, rate: 0.37, tax: 0 }
    ],
    head_of_household: [
      { min: 0, max: 16550, rate: 0.10, tax: 1655 },
      { min: 16551, max: 63100, rate: 0.12, tax: 5586 },
      { min: 63101, max: 100500, rate: 0.22, tax: 8230 },
      { min: 100501, max: 191950, rate: 0.24, tax: 21948 },
      { min: 191951, max: 243700, rate: 0.32, tax: 16560 },
      { min: 243701, max: 609350, rate: 0.35, tax: 127977 },
      { min: 609351, max: Infinity, rate: 0.37, tax: 0 }
    ]
  };

  // 2024 Standard Deductions
  const standardDeductions = {
    single: 14600,
    married: 29200,
    head_of_household: 21900
  };

  // State Tax Brackets (2024 - simplified)
  const stateTaxBrackets = {
    CA: {
      single: [
        { min: 0, max: 10099, rate: 0.01 },
        { min: 10100, max: 23942, rate: 0.02 },
        { min: 23943, max: 37788, rate: 0.04 },
        { min: 37789, max: 52455, rate: 0.06 },
        { min: 52456, max: 66295, rate: 0.08 },
        { min: 66296, max: 338639, rate: 0.093 },
        { min: 338640, max: 406364, rate: 0.103 },
        { min: 406365, max: 677275, rate: 0.113 },
        { min: 677276, max: Infinity, rate: 0.123 }
      ],
      married: [
        { min: 0, max: 20198, rate: 0.01 },
        { min: 20199, max: 47884, rate: 0.02 },
        { min: 47885, max: 75576, rate: 0.04 },
        { min: 75577, max: 104910, rate: 0.06 },
        { min: 104911, max: 132590, rate: 0.08 },
        { min: 132591, max: 677278, rate: 0.093 },
        { min: 677279, max: 812728, rate: 0.103 },
        { min: 812729, max: 1354550, rate: 0.113 },
        { min: 1354551, max: Infinity, rate: 0.123 }
      ]
    },
    NY: {
      single: [
        { min: 0, max: 8500, rate: 0.04 },
        { min: 8501, max: 11700, rate: 0.045 },
        { min: 11701, max: 13900, rate: 0.0525 },
        { min: 13901, max: 21400, rate: 0.059 },
        { min: 21401, max: 80650, rate: 0.0645 },
        { min: 80651, max: 215400, rate: 0.0665 },
        { min: 215401, max: 1077550, rate: 0.0685 },
        { min: 1077551, max: Infinity, rate: 0.0882 }
      ],
      married: [
        { min: 0, max: 17150, rate: 0.04 },
        { min: 17151, max: 23600, rate: 0.045 },
        { min: 23601, max: 27900, rate: 0.0525 },
        { min: 27901, max: 43000, rate: 0.059 },
        { min: 43001, max: 161550, rate: 0.0645 },
        { min: 161551, max: 323200, rate: 0.0665 },
        { min: 323201, max: 2155350, rate: 0.0685 },
        { min: 2155351, max: Infinity, rate: 0.0882 }
      ]
    },
    TX: {
      single: [{ min: 0, max: Infinity, rate: 0.00 }],
      married: [{ min: 0, max: Infinity, rate: 0.00 }]
    },
    FL: {
      single: [{ min: 0, max: Infinity, rate: 0.00 }],
      married: [{ min: 0, max: Infinity, rate: 0.00 }]
    },
    OT: {
      single: [{ min: 0, max: Infinity, rate: 0.00 }],
      married: [{ min: 0, max: Infinity, rate: 0.00 }]
    }
  };

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateTaxBrackets();
    }
  }, [inputs, mode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateTaxBrackets = () => {
    const {
      filingStatus,
      taxableIncome,
      state,
      wages,
      investmentIncome,
      businessIncome,
      otherIncome,
      standardDeduction,
      itemizedDeduction,
      useStandardDeduction,
      childTaxCredit,
      earnedIncomeCredit,
      educationCredits,
      age,
      numberOfDependents
    } = inputs;

    // Parse inputs with safe defaults
    const income = parseFloat(taxableIncome) || 0;
    const wageIncome = parseFloat(wages) || 0;
    const investIncome = parseFloat(investmentIncome) || 0;
    const businessInc = parseFloat(businessIncome) || 0;
    const otherInc = parseFloat(otherIncome) || 0;
    const stdDeduction = parseFloat(standardDeduction) || 0;
    const itemDeduction = parseFloat(itemizedDeduction) || 0;
    const childCredit = parseFloat(childTaxCredit) || 0;
    const eic = parseFloat(earnedIncomeCredit) || 0;
    const eduCredits = parseFloat(educationCredits) || 0;
    const dependents = parseInt(numberOfDependents) || 0;

    // Calculate adjusted gross income
    const agi = wageIncome + investIncome + businessInc + otherInc;
    
    // Calculate deduction
    const deduction = useStandardDeduction ? stdDeduction : Math.max(stdDeduction, itemDeduction);
    const taxableInc = Math.max(0, agi - deduction);

    // Calculate federal tax
    const federalTax = calculateProgressiveTax(taxableInc, filingStatus, 'federal');
    const federalBrackets = getApplicableBrackets(taxableInc, filingStatus, 'federal');

    // Calculate state tax
    const stateTax = calculateProgressiveTax(taxableInc, filingStatus, 'state', state);
    const stateBrackets = getApplicableBrackets(taxableInc, filingStatus, 'state', state);

    // Calculate FICA taxes
    const ficaTax = calculateFICATax(wageIncome);

    // Calculate total credits
    const totalCredits = childCredit + eic + eduCredits;
    const taxAfterCredits = Math.max(0, federalTax + stateTax - totalCredits);

    // Calculate total tax liability
    const totalTaxLiability = taxAfterCredits + ficaTax;

    // Calculate rates
    const effectiveTaxRate = agi > 0 ? (totalTaxLiability / agi) * 100 : 0;
    const marginalTaxRate = findMarginalTaxRate(taxableInc, filingStatus, 'federal');

    // Calculate next bracket information
    const nextBracket = findNextBracket(taxableInc, filingStatus, 'federal');

    // Calculate tax savings
    const taxSavings = {
      standardVsItemized: calculateDeductionSavings(stdDeduction, itemDeduction, useStandardDeduction, marginalTaxRate),
      credits: totalCredits
    };

    const resultData = {
      federalBrackets: federalBrackets || [],
      stateBrackets: stateBrackets || [],
      effectiveTaxRate: effectiveTaxRate || 0,
      marginalTaxRate: marginalTaxRate || 0,
      totalTaxLiability: totalTaxLiability || 0,
      afterTaxIncome: (agi - totalTaxLiability) || 0,
      taxBreakdown: {
        federal: federalTax || 0,
        state: stateTax || 0,
        fica: ficaTax || 0,
        additional: 0,
        credits: totalCredits || 0
      },
      nextBracket: nextBracket || {
        threshold: 0,
        additionalTax: 0,
        rate: 0
      },
      taxSavings: taxSavings || {
        standardVsItemized: 0,
        credits: 0
      },
      incomeSummary: {
        agi: agi || 0,
        taxableIncome: taxableInc || 0,
        deduction: deduction || 0,
        totalCredits: totalCredits || 0
      },
      bracketProgression: calculateBracketProgression(taxableInc, filingStatus, 'federal') || []
    };

    setResult(resultData);
  };

  const calculateProgressiveTax = (income, filingStatus, taxType, state = 'CA') => {
    const brackets = taxType === 'federal' 
      ? federalTaxBrackets[filingStatus] 
      : stateTaxBrackets[state]?.[filingStatus] || [];

    let tax = 0;
    let remainingIncome = income || 0;

    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;

      const bracketWidth = bracket.max - bracket.min;
      const taxableInBracket = Math.min(remainingIncome, bracketWidth);
      
      if (taxableInBracket > 0) {
        tax += taxableInBracket * bracket.rate;
        remainingIncome -= taxableInBracket;
      }
    }

    return tax;
  };

  const getApplicableBrackets = (income, filingStatus, taxType, state = 'CA') => {
    const brackets = taxType === 'federal' 
      ? federalTaxBrackets[filingStatus] 
      : stateTaxBrackets[state]?.[filingStatus] || [];

    return brackets.map(bracket => {
      const isCurrentBracket = income >= bracket.min && income <= bracket.max;
      const isPastBracket = income > bracket.max;
      const bracketWidth = bracket.max - bracket.min;
      const taxableInBracket = Math.max(0, Math.min((income || 0) - bracket.min, bracketWidth));
      
      return {
        ...bracket,
        isCurrentBracket,
        isPastBracket,
        taxableInBracket,
        taxInBracket: taxableInBracket * bracket.rate
      };
    });
  };

  const calculateFICATax = (wageIncome) => {
    if (!wageIncome || wageIncome <= 0) return 0;
    
    // 2024 FICA rates
    const socialSecurityRate = 0.062;
    const medicareRate = 0.0145;
    const socialSecurityLimit = 168600;
    
    const socialSecurityTax = Math.min(wageIncome, socialSecurityLimit) * socialSecurityRate;
    const medicareTax = wageIncome * medicareRate;
    
    // Additional Medicare tax for high earners
    const additionalMedicare = wageIncome > 200000 ? (wageIncome - 200000) * 0.009 : 0;
    
    return socialSecurityTax + medicareTax + additionalMedicare;
  };

  const findMarginalTaxRate = (income, filingStatus, taxType, state = 'CA') => {
    const brackets = taxType === 'federal' 
      ? federalTaxBrackets[filingStatus] 
      : stateTaxBrackets[state]?.[filingStatus] || [];

    for (const bracket of brackets) {
      if (income >= bracket.min && income <= bracket.max) {
        return bracket.rate * 100;
      }
    }
    
    return brackets[brackets.length - 1]?.rate * 100 || 0;
  };

  const findNextBracket = (income, filingStatus, taxType, state = 'CA') => {
    const brackets = taxType === 'federal' 
      ? federalTaxBrackets[filingStatus] 
      : stateTaxBrackets[state]?.[filingStatus] || [];

    for (let i = 0; i < brackets.length; i++) {
      if (income >= brackets[i].min && income <= brackets[i].max) {
        const nextBracket = brackets[i + 1];
        if (nextBracket) {
          const threshold = nextBracket.min;
          const additionalTax = (threshold - income) * nextBracket.rate;
          return {
            threshold,
            additionalTax,
            rate: nextBracket.rate * 100
          };
        }
        break;
      }
    }
    
    return {
      threshold: 0,
      additionalTax: 0,
      rate: 0
    };
  };

  const calculateDeductionSavings = (standard, itemized, useStandard, marginalRate) => {
    if (useStandard) return 0;
    const savings = (itemized - standard) * (marginalRate / 100);
    return Math.max(0, savings);
  };

  const calculateBracketProgression = (income, filingStatus, taxType, state = 'CA') => {
    const brackets = taxType === 'federal' 
      ? federalTaxBrackets[filingStatus] 
      : stateTaxBrackets[state]?.[filingStatus] || [];

    const progression = [];
    let cumulativeTax = 0;

    for (const bracket of brackets) {
      const bracketWidth = bracket.max - bracket.min;
      const taxableInBracket = Math.min(Math.max(0, (income || 0) - bracket.min), bracketWidth);
      const taxInBracket = taxableInBracket * bracket.rate;
      cumulativeTax += taxInBracket;

      progression.push({
        bracket: `${(bracket.rate * 100).toFixed(1)}%`,
        range: `$${bracket.min.toLocaleString()} - $${bracket.max === Infinity ? '∞' : bracket.max.toLocaleString()}`,
        taxableAmount: taxableInBracket,
        taxAmount: taxInBracket,
        cumulativeTax,
        isActive: income > bracket.min
      });

      if (income <= bracket.max) break;
    }

    return progression;
  };

  const calculateStateComparison = () => {
    const { filingStatus, taxableIncome } = inputs;
    const income = parseFloat(taxableIncome) || 0;
    
    const states = ['CA', 'NY', 'TX', 'FL'];
    return states.map(state => {
      const tax = calculateProgressiveTax(income, filingStatus, 'state', state);
      return {
        state,
        tax: tax || 0,
        effectiveRate: income > 0 ? (tax / income) * 100 : 0
      };
    });
  };

  const resetCalculator = () => {
    setInputs({
      filingStatus: "single",
      taxableIncome: "75000",
      taxYear: "2024",
      state: "CA",
      wages: "70000",
      investmentIncome: "5000",
      businessIncome: "0",
      otherIncome: "0",
      standardDeduction: "14600",
      itemizedDeduction: "0",
      useStandardDeduction: true,
      childTaxCredit: "0",
      earnedIncomeCredit: "0",
      educationCredits: "0",
      age: "35",
      isBlind: false,
      isDisabled: false,
      numberOfDependents: "0"
    });
    addNotification("Tax bracket calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Taxable Income,$${(result.incomeSummary?.taxableIncome || 0).toFixed(2)}`,
      `Federal Tax,$${(result.taxBreakdown?.federal || 0).toFixed(2)}`,
      `State Tax,$${(result.taxBreakdown?.state || 0).toFixed(2)}`,
      `FICA Tax,$${(result.taxBreakdown?.fica || 0).toFixed(2)}`,
      `Total Tax,$${(result.totalTaxLiability || 0).toFixed(2)}`,
      `Effective Tax Rate,${(result.effectiveTaxRate || 0).toFixed(2)}%`,
      `Marginal Tax Rate,${(result.marginalTaxRate || 0).toFixed(2)}%`,
      `After-Tax Income,$${(result.afterTaxIncome || 0).toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tax-bracket-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Tax bracket data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Tax bracket calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Tax bracket calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      low_income: { 
        filingStatus: "single",
        taxableIncome: "35000",
        state: "TX"
      },
      middle_income: { 
        filingStatus: "married",
        taxableIncome: "125000",
        state: "CA"
      },
      high_income: {
        filingStatus: "single",
        taxableIncome: "250000",
        state: "NY"
      },
      retiree: {
        filingStatus: "married",
        taxableIncome: "60000",
        state: "FL",
        age: "67"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset.replace('_', ' ')} tax scenario applied`, "info");
  };

  // Chart Data with safe access
  const taxBreakdownChartData = {
    labels: ["Federal Tax", "State Tax", "FICA Tax", "After-Tax Income"],
    datasets: [{
      data: [
        result?.taxBreakdown?.federal || 0,
        result?.taxBreakdown?.state || 0,
        result?.taxBreakdown?.fica || 0,
        result?.afterTaxIncome || 0
      ],
      backgroundColor: ["#EF4444", "#F59E0B", "#8B5CF6", "#10B981"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  };

  const bracketProgressionData = result?.bracketProgression || [];
  const progressionChartData = {
    labels: bracketProgressionData.map(b => b.bracket),
    datasets: [
      {
        label: 'Tax in Bracket',
        data: bracketProgressionData.map(b => b.taxAmount || 0),
        backgroundColor: "#EF4444",
        borderColor: "#EF4444",
        borderWidth: 2
      },
      {
        label: 'Cumulative Tax',
        data: bracketProgressionData.map(b => b.cumulativeTax || 0),
        backgroundColor: "#3B82F6",
        borderColor: "#3B82F6",
        borderWidth: 2,
        type: 'line',
        fill: false
      }
    ]
  };

  const stateComparisonData = calculateStateComparison();
  const stateComparisonChartData = {
    labels: stateComparisonData.map(s => s.state),
    datasets: [
      {
        label: 'State Tax Amount',
        data: stateComparisonData.map(s => s.tax || 0),
        backgroundColor: "#8B5CF6",
        borderColor: "#8B5CF6",
        borderWidth: 2
      },
      {
        label: 'Effective Tax Rate (%)',
        data: stateComparisonData.map(s => s.effectiveRate || 0),
        backgroundColor: "#10B981",
        borderColor: "#10B981",
        borderWidth: 2,
        yAxisID: 'y1'
      }
    ]
  };

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Tax Bracket Finder</h1>
        </div>
        <p>Discover your tax brackets, calculate liabilities, and optimize your tax strategy</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Tax Bracket Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        {(calc.result?.marginalTaxRate || 0).toFixed(1)}%
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Bracket Finder'}
              </button>
              <button 
                className={`tab ${activeTab === "states" ? "active" : ""}`}
                onClick={() => setActiveTab("states")}
              >
                <i className="fas fa-map"></i>
                {isMobile ? 'States' : 'State Comparison'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Common Scenarios:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('low_income')}>
                Low Income
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('middle_income')}>
                Middle Income
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('high_income')}>
                High Income
              </button>
              <button className="preset-btn conservative" onClick={() => quickPreset('retiree')}>
                Retiree
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-user"></i>
                    Basic Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="head_of_household">Head of Household</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill"></i> Taxable Income</label>
                      <input 
                        type="number" 
                        name="taxableIncome" 
                        value={inputs.taxableIncome} 
                        onChange={handleChange}
                        placeholder="75000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Tax Year</label>
                      <select
  name="taxYear"
  value={inputs.taxYear || currentYear}
  onChange={handleChange}
>
  {[currentYear, currentYear - 1, currentYear - 2].map((year) => (
    <option key={year} value={year}>
      {year}
    </option>
  ))}
</select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-map"></i> State</label>
                      <select name="state" value={inputs.state} onChange={handleChange}>
                        <option value="CA">California</option>
                        <option value="NY">New York</option>
                        <option value="TX">Texas</option>
                        <option value="FL">Florida</option>
                        <option value="IL">Illinois</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="OH">Ohio</option>
                        <option value="GA">Georgia</option>
                        <option value="NC">North Carolina</option>
                        <option value="MI">Michigan</option>
                        <option value="OT">Other</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-users"></i> Dependents</label>
                      <input 
                        type="number" 
                        name="numberOfDependents" 
                        value={inputs.numberOfDependents} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-birthday-cake"></i> Age</label>
                      <input 
                        type="number" 
                        name="age" 
                        value={inputs.age} 
                        onChange={handleChange}
                        placeholder="35"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-money-bill-wave"></i>
                    Income Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-briefcase"></i> Wages & Salary</label>
                      <input 
                        type="number" 
                        name="wages" 
                        value={inputs.wages} 
                        onChange={handleChange}
                        placeholder="70000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Investment Income</label>
                      <input 
                        type="number" 
                        name="investmentIncome" 
                        value={inputs.investmentIncome} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-business-time"></i> Business Income</label>
                      <input 
                        type="number" 
                        name="businessIncome" 
                        value={inputs.businessIncome} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-plus-circle"></i> Other Income</label>
                      <input 
                        type="number" 
                        name="otherIncome" 
                        value={inputs.otherIncome} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Deductions & Credits
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-receipt"></i> Standard Deduction</label>
                      <input 
                        type="number" 
                        name="standardDeduction" 
                        value={inputs.standardDeduction} 
                        onChange={handleChange}
                        placeholder="14600"
                        disabled
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-list"></i> Itemized Deduction</label>
                      <input 
                        type="number" 
                        name="itemizedDeduction" 
                        value={inputs.itemizedDeduction} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-child"></i> Child Tax Credit</label>
                      <input 
                        type="number" 
                        name="childTaxCredit" 
                        value={inputs.childTaxCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Earned Income Credit</label>
                      <input 
                        type="number" 
                        name="earnedIncomeCredit" 
                        value={inputs.earnedIncomeCredit} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-graduation-cap"></i> Education Credits</label>
                      <input 
                        type="number" 
                        name="educationCredits" 
                        value={inputs.educationCredits} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="useStandardDeduction" 
                          checked={inputs.useStandardDeduction} 
                          onChange={handleChange}
                        />
                        Use Standard Deduction
                      </label>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cog"></i>
                    Additional Factors
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="isBlind" 
                          checked={inputs.isBlind} 
                          onChange={handleChange}
                        />
                        Blind
                      </label>
                    </div>
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="isDisabled" 
                          checked={inputs.isDisabled} 
                          onChange={handleChange}
                        />
                        Disabled
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* States Tab */}
          {activeTab === "states" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-map"></i>
                  State Tax Comparison
                </h3>
                <p>Compare how your tax situation would differ across various states</p>
                
                {stateComparisonData && stateComparisonData.length > 0 && (
                  <div className="strategy-list">
  {stateComparisonData.map((state, index) => (
    <div key={index} className="strategy-item">
      <div className="strategy-header">
        <span className="strategy-name">{state.state}</span>
        <span className="strategy-efficiency">${(state.tax || 0).toFixed(2)}</span>
      </div>
      <div className="strategy-stats">
        <div className="strategy-stat">
          <span>Effective Rate:</span>
          <span>{(state.effectiveRate || 0).toFixed(2)}%</span>
        </div>
        <div className="strategy-stat">
          <span>Tax Difference:</span>
          <span>${((state.tax || 0) - (stateComparisonData[0]?.tax || 0)).toFixed(2)}</span>
        </div>
      </div>
    </div>
  ))}
</div>

                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Bracket Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No bracket calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-percentage"></i>
                          {(calc.result?.marginalTaxRate || 0).toFixed(1)}%
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-money-bill"></i> Income: ${(calc.result?.incomeSummary?.taxableIncome || 0).toFixed(0)}</span>
                        <span><i className="fas fa-user"></i> {calc.inputs?.filingStatus}</span>
                        <span><i className="fas fa-map"></i> {calc.inputs?.state}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "states") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Tax Bracket Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value">
                          {(result.marginalTaxRate || 0).toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Marginal Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {(result.effectiveTaxRate || 0).toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Effective Tax Rate
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${(result.totalTaxLiability || 0).toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Total Tax
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${(result.afterTaxIncome || 0).toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          After-Tax Income
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Tax Breakdown</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-flag-usa"></i> Federal Income Tax:</span>
                        <span>${(result.taxBreakdown?.federal || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-building"></i> State Income Tax:</span>
                        <span>${(result.taxBreakdown?.state || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shield-alt"></i> FICA Taxes:</span>
                        <span>${(result.taxBreakdown?.fica || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Tax Credits:</span>
                        <span className="positive">-${(result.taxBreakdown?.credits || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row highlight">
                        <span><i className="fas fa-file-invoice-dollar"></i> Total Tax Liability:</span>
                        <span>${(result.totalTaxLiability || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Income Summary</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill"></i> Total Income:</span>
                        <span>${(result.incomeSummary?.agi || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Deduction:</span>
                        <span>${(result.incomeSummary?.deduction || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Taxable Income:</span>
                        <span>${(result.incomeSummary?.taxableIncome || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Tax Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Tax Distribution
                      </h3>
                      <Doughnut 
                        data={taxBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Federal Tax Brackets */}
                    <div className="brackets-section">
                      <h3>
                        <i className="fas fa-layer-group"></i>
                        Federal Tax Brackets
                      </h3>
                      <div className="brackets-list">
                        {(result.federalBrackets || []).map((bracket, index) => (
                          <div key={index} className={`bracket-item ${bracket.isCurrentBracket ? 'current' : ''}`}>
                            <div className="bracket-header">
                              <span className="bracket-rate">{((bracket.rate || 0) * 100).toFixed(1)}%</span>
                              <span className="bracket-range">
                                ${(bracket.min || 0).toLocaleString()} - ${bracket.max === Infinity ? '∞' : (bracket.max || 0).toLocaleString()}
                              </span>
                            </div>
                            {(bracket.taxableInBracket || 0) > 0 && (
                              <div className="bracket-details">
                                <div className="bracket-detail">
                                  <span>Taxable in bracket:</span>
                                  <span>${(bracket.taxableInBracket || 0).toFixed(2)}</span>
                                </div>
                                <div className="bracket-detail">
                                  <span>Tax in bracket:</span>
                                  <span>${(bracket.taxInBracket || 0).toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Next Bracket Information */}
                    {result.nextBracket?.threshold > 0 && (
                      <div className="risk-summary">
                        <h4>Next Bracket Information</h4>
                        <div className="risk-cards">
                          <div className="risk-card">
                            <div className="risk-value">
                              ${(result.nextBracket.threshold || 0).toFixed(0)}
                            </div>
                            <div className="risk-label">Next Bracket Threshold</div>
                          </div>
                          <div className="risk-card">
                            <div className="risk-value">
                              {(result.nextBracket.rate || 0).toFixed(1)}%
                            </div>
                            <div className="risk-label">Next Bracket Rate</div>
                          </div>
                          <div className="risk-card">
                            <div className="risk-value">
                              ${(result.nextBracket.additionalTax || 0).toFixed(2)}
                            </div>
                            <div className="risk-label">Additional Tax per $1k</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tax Savings */}
                    <div className="detailed-results">
                      <h4>Tax Savings Opportunities</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Itemized Deduction Savings:</span>
                        <span className="positive">${(result.taxSavings?.standardVsItemized || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Total Credits Applied:</span>
                        <span className="positive">${(result.taxSavings?.credits || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your income details to see tax bracket analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "states" && stateComparisonData && stateComparisonData.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-map"></i>
                  State Tax Comparison
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={stateComparisonChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          type: 'linear',
                          display: true,
                          position: 'left',
                          title: {
                            display: true,
                            text: 'Tax Amount ($)'
                          }
                        },
                        y1: {
                          type: 'linear',
                          display: true,
                          position: 'right',
                          title: {
                            display: true,
                            text: 'Effective Rate (%)'
                          },
                          grid: {
                            drawOnChartArea: false
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="state-summary">
                  <h4>State Comparison Summary</h4><br/>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Lowest Tax State:</span>
                      <span>{stateComparisonData.reduce((min, s) => (s.tax || 0) < (min.tax || 0) ? s : min).state}</span>
                    </div>
                    <div className="summary-item">
                      <span>Highest Tax State:</span>
                      <span>{stateComparisonData.reduce((max, s) => (s.tax || 0) > (max.tax || 0) ? s : max).state}</span>
                    </div>
                    <div className="summary-item">
                      <span>Tax Difference:</span>
                      <span>${(stateComparisonData.reduce((max, s) => (s.tax || 0) > (max.tax || 0) ? s : max).tax - stateComparisonData.reduce((min, s) => (s.tax || 0) < (min.tax || 0) ? s : min).tax || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default TaxBracketFinder;